import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectorRef, SimpleChanges } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { ServiceRoleComponent } from "../../../../../components/pages/roles/components/service/service-role.component";
import { Bot } from "../../../../../models/bot";
import { Role } from "../../../../../models/role";
import { BotControllerService } from "../../../../../services/role-controller/bot-controller.service";
import { RoleControllerService } from "../../../../../services/role-controller/role-controller.service";
import { RoleService } from "../../../../../services/role-controller/role.service";
import {TranslatedBaseError} from "../../../TranslatedBaseError";
import {TranslateService} from "@ngx-translate/core";

@Component({
    selector: "bot-selector",
    templateUrl: "./bot-selector.component.html",
    providers: [RoleControllerService, BotControllerService],
})

export class BotSelectorComponent extends TranslatedBaseError {

    @Input() public role: Role;
    @Input() public serviceId: string;

    // emit for botType and category
    @Output() public changeBotType = new EventEmitter();
    @Output() public changeBotCategory = new EventEmitter();

    public baseUrl: string;
    public searchedCat: string;
    public searchedType: string;
    public selectedBotId: string;
    public availableBots: Bot[];
    public bots: Bot[];
    public selectedBot: Bot;

    public isLoading = false;
    public activeTab: string = "bot-service";

    public mappingReady: boolean = false;

    constructor(private roleController: RoleControllerService,
                private botController: BotControllerService,
                private roleService: RoleService,
                private router: Router,
                private route: ActivatedRoute,
                toastr: ToastrService,
                private changeDetectorRef: ChangeDetectorRef,
                public translateService: TranslateService) {
        super(toastr, translateService);
    }

    @ViewChild(ServiceRoleComponent) public serviceRoleComponent: ServiceRoleComponent;

    public listeningTypeChanged(newType: string): void {
        this.searchedType = newType;
        this.filterType(false);
    }

    public listeningCategoryChanged(newCategory: string): void {
        this.searchedCat = newCategory;
        this.filterCat(false);
    }

    public filterType(notifyChange = true) {
        if (this.searchedType) {
            if (this.searchedCat) { this.searchedCat = ""; }
            if (this.selectedBot) { this.selectedBot = null; }
            if (this.selectedBotId) { this.selectedBotId = ""; }
            this.availableBots = this.bots.filter((b) => b.botType === this.searchedType);
        }
        if (notifyChange) {
            this.changeBotType.emit(this.searchedType);
        }
    }

    public filterCat(notifyChange = true) {
        if (this.selectedBot) { this.selectedBot = null; }
        if (this.selectedBotId) { this.selectedBotId = ""; }
        if (this.searchedCat) {
            this.availableBots = this.bots.filter((b) => b.category === this.searchedCat);
        }
        if (notifyChange) {
            this.changeBotCategory.emit(this.searchedCat);
        }
    }

    public selectBot(event) {
        this.setSelectedBot(this.selectedBotId, true);
    }

    public addNewBot() {
        this.selectedBotId = "";
        this.selectedBot = new Bot();
        this.activeTab = "general";
        this.selectedBot.category = this.searchedCat;
        this.selectedBot.botType = this.searchedType;

    }

    public ngOnInit() {
        this.baseUrl = "/services/" + this.serviceId + "/bots";
        this.getRolesList();
    }

    public updateSelector(): void {
        this.getRolesList();
    }

    public addedNewBot(data) {
        this.selectedBotId = data.id;
        this.selectedBot = data;
        this.bots.push(data);
        this.availableBots.push(data);
        this.role.botId = data.id;
        this.activeTab = "bot-service";
        this.updateRole(this.role);
    }

    public setSelectedBot(bId, updateRole) {
        if (bId) {
            this.selectedBotId = null;
            this.availableBots = [];
            this.botController.getBot(bId).subscribe(
                (data) => {
                    this.selectedBot = data;
                    this.searchedType = this.selectedBot.botType;
                    this.searchedCat = this.selectedBot.category;
                    this.role.botId = bId;
                    this.role.bot = data;
                    this.activeTab = "bot-service";
                    this.availableBots = this.bots
                        .filter((b) => b.botType === this.searchedType && b.category === this.searchedCat);
                    if (updateRole) { this.updateRole(this.role); }
                    this.selectedBotId = bId;
                },
            );
        }
    }

    public updatedRole() {
        if (this.serviceRoleComponent) { this.serviceRoleComponent.resetMapping(); }
      }

    public getRolesList() {
        this.botController.getServiceBots(this.serviceId).subscribe(
            (data) => {
                this.bots = data.sort((a, b) => {
                    const nameA = a.name.toLowerCase();
                    const nameB = b.name.toLowerCase();
                    if (nameA < nameB) {
                      return -1;
                    }
                  });
                if (this.role.botId) {
                    this.selectedBotId = this.role.botId;
                    this.setSelectedBot(this.selectedBotId, false);
                } else {
                    this.availableBots = data;
                    this.selectedBotId = "";
                    this.selectedBot = null;
                }
            },
        );
    }

    public updateRole(role: Role) {
        const successCb = (data: any) => {
            this.isLoading = false;
            this.responseSuccessful("Saved successfully!");
        };
        const errorCb = (error: any) => {
            this.ngOnInit();
            return this.showErrorMessageWithTitle("Error during save/update of role!", error);
        };
        return this.roleController.saveBotRole(role).subscribe(successCb, errorCb);
    }

    public updateBot(bot: Bot) {
        const successCb = (data: any) => {
            this.isLoading = false;
            this.responseSuccessful("Saved successfully!");
        };
        const errorCb = (error: any) => {
            this.ngOnInit();
            return this.showErrorMessageWithTitle("Error during save/update of role!", error);
        };
        return this.botController.saveBot(bot).subscribe(successCb, errorCb);
    }

    public notifyTabChange(tab) {
        if (tab === "bot-service" && !this.mappingReady) { return; }
        this.activeTab = tab;
        if (!this.serviceRoleComponent) { return; }
        this.serviceRoleComponent.togglePanel(this.activeTab === "bot-service");
    }

    public onMappingReady(event): void {
        this.mappingReady = event;
        // fix for initial load
        this.changeDetectorRef.detectChanges();
        this.notifyTabChange("bot-service");
    }
}
